import React, { useMemo } from 'react';
import InputText from '../../../components/agv-forms-input';
import InputRadio from '../../../components/agv-forms-radio';
import InputCombobox from '../../../components/agv-forms-combobox/input-combobox';
import {
  CONSIGNMENT_RULE_TYPES,
  CONSIGNMENT_WEIGHT_UNIT_OPTIONS,
  getConsignmentRuleTypeOptions,
} from '../../../utils/consignment-rule.util';

export default function ConsignmentRuleFormFields({
  itemKey,
  setItemKey,
  packagingId,
  setPackagingId,
  ruleType,
  setRuleType,
  fixedQuantity,
  setFixedQuantity,
  weightDivisor,
  setWeightDivisor,
  weightUnit,
  setWeightUnit,
  isActive,
  setIsActive,
  packagingOptions,
  mode = 'create',
  initialRuleType,
}) {
  const ruleTypeOptions = useMemo(
    () => getConsignmentRuleTypeOptions(initialRuleType || ruleType, mode),
    [initialRuleType, ruleType, mode]
  );

  const isFixedQuantityLocked =
    mode === 'update' &&
    initialRuleType === CONSIGNMENT_RULE_TYPES.FIXED_QUANTITY;

  return (
    <div className="tw-flex tw-flex-col">
      <InputText
        value={itemKey}
        setValue={setItemKey}
        fieldTitle={'Référence produit'}
        isRequired
        emptyError={`Le champ "Référence produit" ne peut être vide.`}
        placeholder={'Référence produit dans les commandes'}
      />

      <InputCombobox
        value={packagingId}
        setValue={setPackagingId}
        fieldTitle={'Type de palette'}
        isRequired
        emptyError={`Le champ "Type de palette" ne peut être vide.`}
        placeholder={'Sélectionner un type de palette'}
        items={packagingOptions}
        style={'tw-mt-4'}
      />

      <InputRadio
        value={ruleType}
        setValue={setRuleType}
        fieldTitle={'Type de règle'}
        items={ruleTypeOptions}
        style={'tw-mt-4'}
      />

      {ruleType === CONSIGNMENT_RULE_TYPES.FIXED_QUANTITY && (
        <InputText
          value={fixedQuantity}
          setValue={setFixedQuantity}
          fieldTitle={'Quantité fixe'}
          isRequired={!isFixedQuantityLocked}
          emptyError={`Le champ "Quantité fixe" ne peut être vide.`}
          placeholder={'Ex : 2'}
          style={'tw-mt-4'}
          disabled={isFixedQuantityLocked}
        />
      )}

      {ruleType === CONSIGNMENT_RULE_TYPES.WEIGHT_FORMULA && (
        <>
          <InputText
            value={weightDivisor}
            setValue={setWeightDivisor}
            fieldTitle={'Diviseur de poids'}
            isRequired
            emptyError={`Le champ "Diviseur de poids" ne peut être vide.`}
            placeholder={'Ex : 2000'}
            style={'tw-mt-4'}
          />
          <InputRadio
            value={weightUnit}
            setValue={setWeightUnit}
            fieldTitle={'Unité de poids'}
            items={CONSIGNMENT_WEIGHT_UNIT_OPTIONS}
            style={'tw-mt-4'}
          />
        </>
      )}

      <InputRadio
        value={isActive}
        setValue={setIsActive}
        fieldTitle={'Statut'}
        items={[
          { name: 'Actif', value: 'true' },
          { name: 'Inactif', value: 'false' },
        ]}
        style={'tw-mt-4'}
      />
    </div>
  );
}

export function buildConsignmentRulePayload({
  itemKey,
  packagingId,
  ruleType,
  fixedQuantity,
  weightDivisor,
  weightUnit,
  isActive,
}) {
  const payload = {
    item_key: itemKey.trim(),
    packaging_id: Number(packagingId),
    rule_type: ruleType,
    is_active: isActive === 'true',
  };

  if (ruleType === CONSIGNMENT_RULE_TYPES.WEIGHT_FORMULA) {
    payload.weight_divisor = Number(weightDivisor);
    payload.weight_unit = weightUnit;
  }

  if (
    ruleType === CONSIGNMENT_RULE_TYPES.FIXED_QUANTITY &&
    fixedQuantity !== ''
  ) {
    payload.fixed_quantity = Number(fixedQuantity);
  }

  return payload;
}

export function isConsignmentRuleFormValid({
  itemKey,
  packagingId,
  ruleType,
  fixedQuantity,
  weightDivisor,
  weightUnit,
}) {
  if (!itemKey?.trim() || !packagingId || !ruleType) return false;

  if (ruleType === CONSIGNMENT_RULE_TYPES.FIXED_QUANTITY) {
    const qty = Number(fixedQuantity);
    return fixedQuantity !== '' && !Number.isNaN(qty) && qty > 0;
  }

  if (ruleType === CONSIGNMENT_RULE_TYPES.WEIGHT_FORMULA) {
    const divisor = Number(weightDivisor);
    return (
      weightDivisor !== '' &&
      !Number.isNaN(divisor) &&
      divisor > 0 &&
      !!weightUnit
    );
  }

  return true;
}
